import React, { useState, useEffect } from "react";
import "./IdentityVerification.css";
import DocumentSelection from "../DocumentSelection";
import PersonalInformation from "../PersonalInformation";
import DocumentCapture from "../DocumentCapture";
import LivenessCapture from "../LivenessCapture";
import PhotoCapture from "../PhotoCapture/PhotoCapture";
import Web3Connect from "../Web3Connect";
import VerificationSuccess from "../VerificationSuccess";
import AddressSelection from "../AddressSelection";
import AddressUpload from "../AddressUpload";
import NfcScan from "../NfcScan";
import { QRCodeSVG } from "qrcode.react";
import {
  createInquiry,
  submitInquiry,
  submitSingleDocument,
  completeInquiry,
  getInquiryDetails,
  getMerchantPermissions,
  getMerchantSettings,
  updateUserMeta,
} from "../../api/kycApi";
import { AddonNames } from "../../constents/constents";

function IdentityVerification() {
  const [currentStep, setCurrentStep] = useState("identity"); // identity, personal, document, capture-front, capture-back, liveness, web3 (commented), success
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedAddressDoc, setSelectedAddressDoc] = useState("utility-bill");
  const [capturedImages, setCapturedImages] = useState({
    front: null,
    back: null,
    photo: null,
  });
  const [livenessVideo, setLivenessVideo] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [merchantConfigs, setMerchantConfigs] = useState([]);
  const [web3Enabled, setWeb3Enabled] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [token, setToken] = useState(null);
  const [id, setId] = useState(null);
  const [hasValidParams, setHasValidParams] = useState(true);
  const [isUnknownMerchant, setIsUnknownMerchant] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    fullName: "",
    dateOfBirth: "",
    country: "",
  });
  const [submittedDocuments, setSubmittedDocuments] = useState({
    front: false,
    back: false,
    photo: false,
    video: false,
    utilityBill: false,
  });
  const [personalInfoError, setPersonalInfoError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  // Get token and id from URL query parameters
  useEffect(() => {
    // Try standard query params first (with ?)
    let urlParams = new URLSearchParams(window.location.search);
    let urlToken = urlParams.get("token");
    let urlId = urlParams.get("id");

    // If not found, try parsing from full URL (handles case without ?)
    if (!urlToken && !urlId) {
      const fullUrl = window.location.href;
      const tokenMatch = fullUrl.match(/[?&/]token=([^&]+)/);
      const idMatch = fullUrl.match(/[?&/]id=([^&]+)/);

      if (tokenMatch) {
        urlToken = decodeURIComponent(tokenMatch[1]);
      }
      if (idMatch) {
        urlId = decodeURIComponent(idMatch[1]);
      }
    }

    // Check if both token and id are present
    if (!urlToken || !urlId) {
      setHasValidParams(false);
    } else {
      setToken(urlToken);
      setId(urlId);
      setHasValidParams(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !token || !hasValidParams) return;

      setIsLoading(true);
      try {
        const [permissionsData, settingsData] = await Promise.all([
          getMerchantPermissions(token),
          getMerchantSettings(token),
        ]);

        setPermissions(permissionsData?.allAddons);
        setWeb3Enabled(permissionsData?.isWeb3Enabled);
        setMerchantConfigs(settingsData?.settings || []);
      } catch (error) {
        // If we get 401, show unknown merchant error
        const status = error?.response?.status;
        if (status === 401) {
          console.error("Unauthorized / unknown merchant:", error);
          setIsUnknownMerchant(true);
          setHasValidParams(false);
        } else {
          console.error("Error fetching merchant data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, token, hasValidParams]);

  // console.log("permissions", permissions);
  // console.log("web3Enabled", web3Enabled);

  useEffect(() => {
    const fetchInquiryDetails = async () => {
      if (isDesktop) return; // Desktop par step change mat karo — sirf QR dikhao
      if (!id || !token || !hasValidParams || !permissions) return;

      setIsLoading(true);
      try {
        const response = await getInquiryDetails(id, token);

        if (response?.statusCode === 200 && response?.data?.users) {
          const { user, userDetail, web3Details } = response.data.users;

          // If kycStatus is SUCCESS and aiStatus is ANALYZED, go straight to success step
          if (user?.kycStatus === "SUCCESS" && user?.aiStatus === "ANALYZED") {
            setCurrentStep("success");
            setIsLoading(false);
            return;
          }
          if (
            user?.kycStatus === "MANUAL_REVIEW_REQUIRED" &&
            user?.aiStatus === "ANALYZED"
          ) {
            setCurrentStep("success");
            setIsLoading(false);
            return;
          }

          // Always use id from URL (tenantUserId) for API calls
          // This ensures we use the correct ID that matches the URL parameter
          if (id) {
            setSubmissionId(id);
          } else if (user?.tenantUserId) {
            // Fallback to tenantUserId from API if id not available
            setSubmissionId(user.tenantUserId);
          }

          // Pre-fill personal information if available
          if (user?.userMeta) {
            const userMeta = user.userMeta;
            if (userMeta.fullName || userMeta.dateOfBirth || userMeta.country) {
              setPersonalInfo({
                fullName: userMeta.fullName || "",
                dateOfBirth: userMeta.dateOfBirth || "",
                country: userMeta.country || "Pakistan",
              });
            }
          }

          // Check which documents have been submitted
          const submittedDocs = {
            idFront: false,
            idBack: false,
            passport: false,
            video: false,
            photo: false,
            utilityBill: false,
          };

          if (userDetail && Array.isArray(userDetail)) {
            userDetail.forEach((detail) => {
              if (detail.documentType === "idFront") {
                submittedDocs.idFront = true;
              } else if (detail.documentType === "idBack") {
                submittedDocs.idBack = true;
              } else if (detail.documentType === "passport") {
                submittedDocs.passport = true;
              } else if (detail.documentType === "photo") {
                submittedDocs.photo = true;
              } else if (detail.documentType === "video") {
                submittedDocs.video = true;
              } else if (detail.documentType === "drivingLicenceFront") {
                submittedDocs.idFront = true;
              } else if (detail.documentType === "drivingLicenceBack") {
                submittedDocs.idBack = true;
              } else if (detail.documentType === "utilityBill") {
                submittedDocs.utilityBill = true;
              }
            });
          }

          if (user?.nfcVerification) {
            submittedDocs.idFront = true;
            submittedDocs.idBack = true;
          }

          // Update submitted documents state
          setSubmittedDocuments({
            front: submittedDocs.idFront || submittedDocs.passport,
            back: submittedDocs.idBack,
            photo: submittedDocs.photo,
            video: submittedDocs.video,
            utilityBill: submittedDocs.utilityBill,
          });

          // Determine document type based on submitted documents
          if (user?.userMeta?.documentType) {
            setSelectedDocument(user.userMeta.documentType);
          } else if (submittedDocs.passport) {
            setSelectedDocument("passport");
          } else if (
            submittedDocs.idFront &&
            userDetail.some((d) => d.documentType === "drivingLicenceFront")
          ) {
            setSelectedDocument("drivers-license");
          } else if (submittedDocs.idFront || submittedDocs.idBack) {
            // Could be national-id or drivers-license, default to national-id if not stored
            setSelectedDocument("national-id");
          }

          // If all docs including video/photo are present but KYC is not submitted
          const hasPhotoOrVideo = submittedDocs.video || submittedDocs.photo;
          const hasAddressProofPermission = permissions?.includes(AddonNames.PROOF_OF_ADDRESS);
          const isNfcFlow = !!(user?.nfcVerification);

          if (hasPhotoOrVideo && user?.kycStatus !== "SUBMITTED") {
            if (hasAddressProofPermission && !submittedDocs.utilityBill) {
              setCurrentStep("address-selection");
              return;
            }
            if (
              permissions?.includes(AddonNames.WEB3_INTEGRATION) &&
              web3Enabled &&
              !web3Details?.signedMessage
            ) {
              setCurrentStep("web3");
              return;
            } else {
              try {
                const completeResponse = await completeInquiry(id, token);
                const isCompleteSuccess =
                  completeResponse?.status === 200 ||
                  completeResponse?.status === 201 ||
                  completeResponse?.statusCode === 200 ||
                  completeResponse?.statusCode === 201 ||
                  completeResponse?.data?.statusCode === 200 ||
                  completeResponse?.data?.statusCode === 201;

                if (isCompleteSuccess) {
                  setCurrentStep("success");
                  return;
                }
              } catch (completeError) {
                console.error("Auto-complete inquiry error:", completeError);
              }
            }
          }

          let nextStep = "identity"; // Default to start

          const hasLivenessPermission = permissions?.includes(
            AddonNames.LIVENESS_DETECTION
          );

          // ── NFC resume path: nfcData already stored → proceed to photo/selfie ──
          if (isNfcFlow) {
            if (!submittedDocs.photo) {
              nextStep = "capture-photo";
            } else if (!submittedDocs.video && hasLivenessPermission) {
              nextStep = "liveness";
            } else if (hasAddressProofPermission && !submittedDocs.utilityBill) {
              nextStep = "address-selection";
            } else if (
              permissions?.includes(AddonNames.WEB3_INTEGRATION) &&
              web3Enabled &&
              (!web3Details?.signedMessage ||
                Object.keys(web3Details.signedMessage).length === 0)
            ) {
              nextStep = "web3";
            } else {
              nextStep = "success";
            }
            setCurrentStep(nextStep);
            return;
          }

          // ── Standard camera-based resume path ──
          // If KYC is already completed, show success
          if (
            user?.kycStatus === "SUBMITTED" &&
            (submittedDocs.video || !hasLivenessPermission)
          ) {
            if (hasAddressProofPermission && !submittedDocs.utilityBill) {
              nextStep = "address-selection";
            } else {
              nextStep =
                permissions?.includes(AddonNames.WEB3_INTEGRATION) &&
                  web3Enabled &&
                  (!web3Details?.signedMessage ||
                    Object.keys(web3Details.signedMessage).length === 0)
                  ? "web3"
                  : "success";
            }
          }
          // If liveness is missing or required
          else if (!submittedDocs.video && hasLivenessPermission) {
            // Check if photo is missing first
            if (!submittedDocs.photo && !hasLivenessPermission) {
              // Check if we need back document first (only for non-passport)
              if (
                !submittedDocs.passport &&
                !submittedDocs.idBack &&
                submittedDocs.idFront
              ) {
                nextStep = "capture-back";
              } else if (
                submittedDocs.idFront ||
                submittedDocs.passport ||
                submittedDocs.idBack
              ) {
                nextStep = "capture-photo";
              } else {
                if (user?.userMeta?.fullName) {
                  nextStep = "document";
                } else {
                  nextStep = "personal";
                }
              }
            } else if (hasLivenessPermission) {
              if (
                !submittedDocs.passport &&
                !submittedDocs.idBack &&
                submittedDocs.idFront
              ) {
                nextStep = "capture-back";
              } else if (
                submittedDocs.idFront ||
                submittedDocs.passport ||
                submittedDocs.idBack
              ) {
                nextStep = "liveness";
              } else {
                if (user?.userMeta?.fullName) {
                  nextStep = "document";
                } else {
                  nextStep = "personal";
                }
              }
            } else {
              nextStep = "liveness";
            }
          }
          // If liveness is NOT required and video is missing
          else if (!submittedDocs.video && !hasLivenessPermission) {
            if (!submittedDocs.photo) {
              if (
                !submittedDocs.passport &&
                !submittedDocs.idBack &&
                submittedDocs.idFront
              ) {
                nextStep = "capture-back";
              } else if (
                submittedDocs.idFront ||
                submittedDocs.passport ||
                submittedDocs.idBack
              ) {
                nextStep = "capture-photo";
              } else if (user?.userMeta?.fullName) {
                nextStep = "document";
              } else {
                nextStep = "personal";
              }
            } else {
              if (hasAddressProofPermission && !submittedDocs.utilityBill) {
                nextStep = "address-selection";
              } else {
                nextStep =
                  permissions?.includes(AddonNames.WEB3_INTEGRATION) &&
                    web3Enabled &&
                    (!web3Details?.signedMessage ||
                      Object.keys(web3Details.signedMessage).length === 0)
                    ? "web3"
                    : "success";
              }
            }
          }
          // If back document is missing (and not passport, and front exists)
          else if (
            !submittedDocs.idBack &&
            !submittedDocs.passport &&
            submittedDocs.idFront
          ) {
            nextStep = "capture-back";
          }
          // If front document is missing (and not passport)
          else if (!submittedDocs.idFront && !submittedDocs.passport) {
            if (user?.userMeta?.fullName) {
              nextStep = "document";
            } else {
              nextStep = "personal";
            }
          }
          // If personal info is missing
          else if (!user?.userMeta?.fullName) {
            nextStep = "personal";
          } else {
            nextStep = "identity";
          }

          setCurrentStep(nextStep);
        }
      } catch (error) {
        // If we get 401, show unknown merchant error
        const status = error?.response?.status;
        if (status === 401) {
          console.error("Unauthorized / unknown merchant:", error);
          setIsUnknownMerchant(true);
          setHasValidParams(false);
        } else {
          console.error("Error fetching inquiry details:", error);
          // For other errors, keep existing behavior (stay on identity, allow fresh start)
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInquiryDetails();
  }, [id, token, hasValidParams, permissions, isDesktop]);

  useEffect(() => {
    const checkDevice = () => {
      // Check if it's a touch device (mobile/tablet)
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      // Check screen width (desktop typically > 768px)
      const isWideScreen = window.innerWidth > 768;
      // Check user agent for mobile devices
      const isMobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Desktop if: not a touch device OR (wide screen AND not mobile user agent)
      const desktop = !isTouchDevice || (isWideScreen && !isMobileUserAgent);

      setIsDesktop(desktop);
      setCurrentUrl(window.location.href);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const handleStartVerification = async () => {
    if (isDesktop) return; // Desktop par verification start mat karo
    // Always use id from URL (tenantUserId) for API calls
    // Set submissionId to id from URL if not already set
    if (!submissionId && id) {
      setSubmissionId(id);
    }

    // If submissionId already exists, skip creating new inquiry
    if (submissionId) {
      setCurrentStep("personal");
      return;
    }

    setIsLoading(true);
    try {
      // Use id from URL (tenantUserId) for creating inquiry
      const inquiryId = id;
      const response = await createInquiry(inquiryId, token);

      // Use the id from URL as submissionId (not the response ID)
      if (id) {
        setSubmissionId(id);
      }

      setCurrentStep("personal");
    } catch (error) {
      console.error(" Error creating inquiry:", error);
      // If inquiry already exists, try to fetch details
      try {
        const detailsResponse = await getInquiryDetails(id, token);
        if (detailsResponse?.statusCode === 200) {
          // Always use id from URL (tenantUserId) for API calls
          if (id) {
            setSubmissionId(id);
          }
          setCurrentStep("personal");
        }
      } catch (fetchError) {
        console.error("Error fetching existing inquiry:", fetchError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (currentUrl) {
      navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handlePersonalBack = () => {
    setCurrentStep("identity");
  };

  const handlePersonalContinue = async (formData) => {
    setPersonalInfo(formData);
    setPersonalInfoError(null);
    setIsLoading(true);
    try {
      await updateUserMeta(submissionId, formData, token);
      setCurrentStep("document");
    } catch (error) {
      console.error("Error updating user meta:", error);
      setPersonalInfoError(
        error.response?.data?.message ||
        "Failed to update personal information. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isNfcEnabled =
    permissions?.includes(AddonNames.NFC_VERIFICATION) ||
    merchantConfigs?.some(
      (c) =>
        c.name === "nfc_verification" &&
        (c.value?.value === true || c.value === true)
    );

  const handleDocumentBack = () => {
    setCurrentStep("personal");
  };

  const handleDocumentContinue = (docType, country) => {
    setSelectedDocument(docType);
    if (isNfcEnabled) {
      setCurrentStep("nfc-scan");
    } else {
      setCurrentStep("capture-front");
    }
  };

  const handleNfcScanBack = () => {
    setCurrentStep("document");
  };

  const handleNfcScanContinue = async (nfcData, nfcChipImage) => {
    setIsLoading(true);
    try {
      const documentData = {
        nfcData: nfcData,
      };
      if (nfcChipImage) {
        documentData.nfcChipImage = nfcChipImage;
      }

      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, front: true, back: true }));
        // After NFC scan, proceed directly to Photo/Selfie capture
        setCurrentStep("capture-photo");
      } else {
        console.error("Failed to submit NFC data:", response);
        alert("Failed to submit NFC data. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting NFC data:", error);
      alert("Error submitting NFC data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptureFrontBack = () => {
    setCurrentStep("document");
  };

  const handleCaptureFrontContinue = async (image, side) => {
    setCapturedImages((prev) => ({ ...prev, front: image }));

    // Skip submission if already submitted
    if (submittedDocuments.front) {
      // If back side needed
      if (selectedDocument !== "passport") {
        setCurrentStep("capture-back");
      } else {
        // For passport, move to capture-photo
        setCurrentStep("capture-photo");
      }
      return;
    }

    setIsLoading(true);

    try {
      // Determine document field name based on document type
      let documentField = "idFront";
      if (selectedDocument === "passport") {
        documentField = "passport";
      } else if (selectedDocument === "drivers-license") {
        documentField = "drivingLicenceFront";
      }

      // Prepare document data - include personal info on first submission
      const documentData = {
        [documentField]: image,
      };

      // Include personal info only if this is the first document submission
      if (!submittedDocuments.front && !submittedDocuments.back) {
        documentData.fullName = personalInfo.fullName;
        documentData.country = personalInfo.country;
        documentData.dateOfBirth = personalInfo.dateOfBirth;
        documentData.documentType = selectedDocument; // Store the specific document type
      }

      // Submit front document
      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      // Check both HTTP status and statusCode in response
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, front: true }));

        // Only move to next step on success
        if (selectedDocument !== "passport") {
          setCurrentStep("capture-back");
        } else {
          // For passport, move to liveness if permitted, otherwise capture-photo
          if (permissions?.includes(AddonNames.LIVENESS_DETECTION)) {
            setCurrentStep("liveness");
          } else {
            setCurrentStep("capture-photo");
          }
        }
      } else {
        console.error("Failed to submit front document:", response);
        alert("Failed to submit document. Please try again.");
        // Stay on current step - don't proceed
      }
    } catch (error) {
      console.error("Error submitting front document:", error);
      alert(
        "Error submitting document. Please check your connection and try again."
      );
      // Stay on current step - don't proceed
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptureBackBack = () => {
    setCurrentStep("capture-front");
  };

  const handleCaptureBackContinue = async (image, side) => {
    setCapturedImages((prev) => ({ ...prev, back: image }));

    // Skip submission if already submitted
    if (submittedDocuments.back) {
      setCurrentStep("capture-photo");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare document data - only the back document (personal info already sent)
      const documentField =
        selectedDocument === "drivers-license"
          ? "drivingLicenceBack"
          : "idBack";

      const documentData = {
        [documentField]: image,
      };

      // Submit back document
      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      // Check both HTTP status and statusCode in response
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, back: true }));
        // Back doc done — always go to Photo/Selfie next
        setCurrentStep("capture-photo");
      } else {
        console.error("Failed to submit back document:", response);
        alert("Failed to submit document. Please try again.");
        // Stay on current step - don't proceed
      }
    } catch (error) {
      console.error("Error submitting back document:", error);
      alert(
        "Error submitting document. Please check your connection and try again."
      );
      // Stay on current step - don't proceed
    } finally {
      setIsLoading(false);
    }
  };

  const handleCapturePhotoBack = () => {
    if (isNfcEnabled) {
      setCurrentStep("nfc-scan");
    } else if (selectedDocument === "passport") {
      setCurrentStep("capture-front");
    } else {
      setCurrentStep("capture-back");
    }
  };

  const handleCapturePhotoContinue = async (image) => {
    setCapturedImages((prev) => ({ ...prev, photo: image }));

    // Skip submission if already submitted
    if (submittedDocuments.photo) {
      setCurrentStep("liveness");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare document data
      const documentData = {
        photo: image,
      };

      // Submit photo
      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      // Check both HTTP status and statusCode in response
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, photo: true }));

        // Correct order: Photo → Liveness → POA → Web3 → Complete
        if (permissions?.includes(AddonNames.LIVENESS_DETECTION)) {
          setCurrentStep("liveness");
        } else if (permissions?.includes(AddonNames.PROOF_OF_ADDRESS) && !submittedDocuments.utilityBill) {
          setCurrentStep("address-selection");
        } else if (permissions?.includes(AddonNames.WEB3_INTEGRATION) && web3Enabled) {
          setCurrentStep("web3");
        } else {
          // No liveness, no POA, no Web3 — complete directly
          setIsLoading(true);
          try {
            const completeResponse = await completeInquiry(submissionId, token);
            const isCompleteSuccess =
              completeResponse?.status === 200 ||
              completeResponse?.status === 201 ||
              completeResponse?.statusCode === 200 ||
              completeResponse?.statusCode === 201 ||
              completeResponse?.data?.statusCode === 200 ||
              completeResponse?.data?.statusCode === 201;

            if (isCompleteSuccess) {
              setCurrentStep("success");
            } else {
              console.error(
                "Failed to auto-complete inquiry:",
                completeResponse
              );
              alert(
                "Photo submitted but verification completion failed. Please try again."
              );
            }
          } catch (completeError) {
            console.error("Error auto-completing inquiry:", completeError);
            alert(
              "Photo submitted but error completing verification. Please try again."
            );
          } finally {
            setIsLoading(false);
          }
        }
      } else {
        console.error("Failed to submit photo:", response);
        alert("Failed to submit photo. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting photo:", error);
      alert(
        "Error submitting photo. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLivenessBack = () => {
    // Liveness is always after photo capture — go back to photo
    setCurrentStep("capture-photo");
  };

  const handleLivenessContinue = async (videoBlob) => {
    setLivenessVideo(videoBlob);

    // Skip submission if already submitted, just complete inquiry
    if (submittedDocuments.video) {
      if (permissions?.includes(AddonNames.PROOF_OF_ADDRESS) && !submittedDocuments.utilityBill) {
        setCurrentStep("address-selection");
      } else if (permissions?.includes(AddonNames.WEB3_INTEGRATION) && web3Enabled) {
        setCurrentStep("web3");
      } else {
        setIsLoading(true);
        try {
          const completeResponse = await completeInquiry(submissionId, token);

          // Log the response for debugging
          console.log(
            "Complete inquiry response (already submitted):",
            completeResponse
          );

          // Check both status and statusCode (including 201)
          const isCompleteSuccess =
            completeResponse?.status === 200 ||
            completeResponse?.status === 201 ||
            completeResponse?.statusCode === 200 ||
            completeResponse?.statusCode === 201 ||
            completeResponse?.data?.statusCode === 200 ||
            completeResponse?.data?.statusCode === 201;

          if (isCompleteSuccess) {
            setCurrentStep("success");
          } else {
            console.error(
              "Failed to complete inquiry - Response:",
              completeResponse
            );
            alert("Failed to complete verification. Please try again.");
          }
        } catch (error) {
          console.error("Error completing inquiry:", error);
          alert(
            "Error completing verification. Please check your connection and try again."
          );
        } finally {
          setIsLoading(false);
        }
      }
      return;
    }

    setIsLoading(true);

    try {
      // Prepare document data - only the video (personal info and documents already sent)
      const documentData = {
        video: videoBlob,
      };

      // Submit liveness video
      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      // Check both HTTP status and statusCode in response
      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, video: true }));

        if (permissions?.includes(AddonNames.PROOF_OF_ADDRESS) && !submittedDocuments.utilityBill) {
          setCurrentStep("address-selection");
        } else if (permissions?.includes(AddonNames.WEB3_INTEGRATION) && web3Enabled) {
          setCurrentStep("web3");
        } else {
          // Complete inquiry after video submission
          try {
            const completeResponse = await completeInquiry(submissionId, token);

            // Log the response for debugging
            console.log("Complete inquiry response:", completeResponse);

            const isCompleteSuccess =
              completeResponse?.status === 200 ||
              completeResponse?.status === 201 ||
              completeResponse?.statusCode === 200 ||
              completeResponse?.statusCode === 201 ||
              completeResponse?.data?.statusCode === 200 ||
              completeResponse?.data?.statusCode === 201;

            if (isCompleteSuccess) {
              setCurrentStep("success");
            } else {
              console.error(
                "Failed to complete inquiry - Response:",
                completeResponse
              );
              alert(
                "Video submitted successfully but failed to complete verification. Please try again."
              );
            }
          } catch (completeError) {
            console.error("Error completing inquiry:", completeError);
            alert(
              "Video submitted successfully but error completing verification. Please check your connection and try again."
            );
          }
        }
      } else {
        console.error("Failed to submit liveness video:", response);
        alert("Failed to submit video. Please try again.");
        // Stay on current step - don't proceed
      }
    } catch (error) {
      console.error("Error submitting liveness video:", error);
      alert(
        "Error submitting video. Please check your connection and try again."
      );
      // Stay on current step - don't proceed
    } finally {
      setIsLoading(false);
    }
  };

  const handleWeb3Back = () => {
    if (permissions?.includes(AddonNames.PROOF_OF_ADDRESS)) {
      setCurrentStep("address-upload");
    } else if (permissions?.includes(AddonNames.LIVENESS_DETECTION)) {
      setCurrentStep("liveness");
    } else {
      setCurrentStep("capture-photo");
    }
  };

  const handleAddressSelectionBack = () => {
    if (permissions?.includes(AddonNames.LIVENESS_DETECTION)) {
      setCurrentStep("liveness");
    } else {
      setCurrentStep("capture-photo");
    }
  };

  const handleAddressSelectionContinue = (docType) => {
    setSelectedAddressDoc(docType);
    setCurrentStep("address-upload");
  };

  const handleAddressUploadBack = () => {
    setCurrentStep("address-selection");
  };

  const handleAddressUploadContinue = async (file) => {
    setIsLoading(true);
    try {
      const documentData = {};
      if (selectedAddressDoc === "utility-bill") {
        documentData.utilityBill = file;
      } else if (selectedAddressDoc === "bank-statement") {
        documentData.bankStatement = file;
      } else if (selectedAddressDoc === "govt-letter") {
        documentData.governmentIssuedDocument = file;
      }

      const response = await submitSingleDocument(
        submissionId,
        documentData,
        token
      );

      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.statusCode === 200 ||
        response?.statusCode === 201;

      if (isSuccess) {
        setSubmittedDocuments((prev) => ({ ...prev, utilityBill: true }));

        if (permissions?.includes(AddonNames.WEB3_INTEGRATION) && web3Enabled) {
          setCurrentStep("web3");
        } else {
          try {
            const completeResponse = await completeInquiry(submissionId, token);
            const isCompleteSuccess =
              completeResponse?.status === 200 ||
              completeResponse?.status === 201 ||
              completeResponse?.statusCode === 200 ||
              completeResponse?.statusCode === 201 ||
              completeResponse?.data?.statusCode === 200 ||
              completeResponse?.data?.statusCode === 201;

            if (isCompleteSuccess) {
              setCurrentStep("success");
            } else {
              console.error("Failed to complete inquiry:", completeResponse);
              alert("Verification complete failed. Please try again.");
            }
          } catch (completeError) {
            console.error("Error auto-completing inquiry:", completeError);
            alert("Error completing verification. Please try again.");
          }
        }
      } else {
        console.error("Failed to submit utility bill:", response);
        alert("Failed to submit document. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting utility bill:", error);
      alert("Error submitting document. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleWeb3Continue = () => {
    setCurrentStep("success");
  };

  const handleFinish = () => {
    // Reset to start or redirect to dashboard
    setCurrentStep("identity");
    // Or you can redirect: window.location.href = '/dashboard';
  };

  // --------- COMPONENT RENDERING ---------
  // Show error if token or id is missing
  if (!hasValidParams && currentStep === "identity") {
    return (
      <div className="identity-container">
        <div className="identity-card">
          <div className="identity-header">
            <div className="identity-logo">
              <img src="images/verifilite.svg" alt="VerifiLite" />
            </div>
          </div>

          <div className="error-content">
            <div className="error-icon">
              <svg viewBox="0 0 100 100" fill="none">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#dc2626"
                  strokeWidth="3"
                  fill="none"
                />
                <path
                  d="M50 30 L50 60"
                  stroke="#dc2626"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                <circle cx="50" cy="70" r="3" fill="#dc2626" />
              </svg>
            </div>
            <h2 className="error-title">Unable to Process</h2>
            <p className="error-message">
              {isUnknownMerchant
                ? "Unknown merchant"
                : "Sorry, we are unable to process you."}
            </p>
          </div>

          <div className="footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  if (currentStep === "personal") {
    return (
      <PersonalInformation
        onBack={handlePersonalBack}
        onContinue={handlePersonalContinue}
        initialData={personalInfo}
        error={personalInfoError}
        ageLimit={
          merchantConfigs?.find((c) => c.name === "age_limit")?.value?.value
        }
      />
    );
  }

  if (currentStep === "document") {
    return (
      <DocumentSelection
        onBack={handleDocumentBack}
        onContinue={handleDocumentContinue}
      />
    );
  }

  if (currentStep === "nfc-scan") {
    return (
      <NfcScan
        onBack={handleNfcScanBack}
        onContinue={handleNfcScanContinue}
        isSubmitting={isLoading}
        personalInfo={personalInfo}
      />
    );
  }

  if (currentStep === "capture-front") {
    return (
      <DocumentCapture
        key="front" // ✅ ensures fresh component mount
        documentType={selectedDocument}
        side="front"
        onBack={handleCaptureFrontBack}
        onContinue={handleCaptureFrontContinue}
        isSubmitting={isLoading}
      />
    );
  }

  if (currentStep === "capture-back") {
    return (
      <DocumentCapture
        key="back" // ✅ ensures re-initialization
        documentType={selectedDocument}
        side="back"
        onBack={handleCaptureBackBack}
        onContinue={handleCaptureBackContinue}
        isSubmitting={isLoading}
      />
    );
  }

  if (currentStep === "capture-photo") {
    return (
      <PhotoCapture
        onBack={handleCapturePhotoBack}
        onContinue={handleCapturePhotoContinue}
        isSubmitting={isLoading}
      />
    );
  }

  if (currentStep === "liveness") {
    return (
      <LivenessCapture
        onBack={handleLivenessBack}
        onContinue={handleLivenessContinue}
        isSubmitting={isLoading}
      />
    );
  }

  if (currentStep === "address-selection") {
    return (
      <AddressSelection
        onBack={handleAddressSelectionBack}
        onContinue={handleAddressSelectionContinue}
      />
    );
  }

  if (currentStep === "address-upload") {
    return (
      <AddressUpload
        onBack={handleAddressUploadBack}
        onContinue={handleAddressUploadContinue}
        isSubmitting={isLoading}
      />
    );
  }

  if (currentStep === "web3") {
    return (
      <Web3Connect
        inquiryId={submissionId}
        onBack={handleWeb3Back}
        onContinue={handleWeb3Continue}
        token={token}
      />
    );
  }

  if (currentStep === "success") {
    return <VerificationSuccess onFinish={handleFinish} />;
  }

  // ---------- DEFAULT IDENTITY PAGE ----------
  return (
    <div className="identity-container">
      <div className="identity-card">
        <div className="identity-header">
          <div className="identity-logo">
            <img src="images/verifilite.svg" alt="VerifiLite" />
          </div>
        </div>

        <h2 className="identity-title">Identity Verification</h2>
        <p className="identity-subtitle">
          Follow these quick steps to confirm your identity
        </p>

        <div className="steps-container">
          <div className="progress-line">
            <div className="progress-fill"></div>
          </div>

          <div className="steps">
            <div className="step">
              <span className="step-label me-5">STEP 1</span>
              <span className="step-text">ID Verification</span>
            </div>
            <div className="step">
              <span className="step-label me-5">STEP 2</span>
              <span className="step-text me-4">Liveness</span>
            </div>
          </div>
        </div>

        <p className="identity-note">
          Use VerifiLite ID to skip ahead and speed up next time
        </p>

        {isDesktop ? (
          <div className="desktop-view-container">
            <button
              className="verify-btn secondary mb-4"
              onClick={() => setShowMobileModal(true)}
            >
              <svg
                className="btn-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ width: "18px", height: "18px" }}
              >
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                <line x1="12" y1="18" x2="12.01" y2="18"></line>
              </svg>
              Continue on Mobile
            </button>
            <p className="desktop-info-text">
              We recommend using a mobile device for a better verification
              experience.
            </p>
          </div>
        ) : (
          <>
            <button
              className="verify-btn"
              onClick={handleStartVerification}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  {submissionId ? "Resuming..." : "Loading..."}
                </>
              ) : submissionId ? (
                "Continue Verification"
              ) : (
                "Start Verification"
              )}
            </button>

            <p className="privacy-text">
              By continuing, you acknowledge our Privacy Notice
            </p>
          </>
        )}

        {isDesktop && showMobileModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowMobileModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Verification Redirect</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowMobileModal(false)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="desktop-url-container modal-adjust">
                <p className="desktop-message">
                  Please scan the QR code or copy the link below to continue on
                  your mobile device.
                </p>
                <div className="qr-section">
                  <div className="qr-wrapper">
                    <QRCodeSVG
                      value={currentUrl}
                      size={200}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                  <p className="qr-note">Scan to open verification link</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="footer">Secured by VerifiLite</div>
      </div>
    </div>
  );
}

export default IdentityVerification;
